import React from 'react';
import { Skeleton } from 'components/ui/skeleton';
import { AraUserSortKey, AraUserTableRow } from '../api';
import {
    ChevronUp,
    ChevronDown,
    ChevronsLeft,
    ChevronsRight,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';

interface Props {
    data: AraUserTableRow[];
    loading: boolean;
    customerName: string;
    onUserClick: (ecode: string, name: string) => void;
    onBack: () => void;
    conversionType: 'INR' | 'Lakhs' | 'Crores';
    onConversionChange: (v: 'INR' | 'Lakhs' | 'Crores') => void;
    page: number;
    pageSize: number;
    totalItems: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    sortBy: AraUserSortKey;
    sortOrder: 'asc' | 'desc';
    onSortChange: (key: AraUserSortKey) => void;
}

const columns: Array<{
    key: AraUserSortKey;
    label: string;
    numeric: boolean;
}> = [
        { key: 'collection_responsibility', label: 'Collection Resp.', numeric: false },
        { key: 'number_of_bills', label: 'No. of Bills', numeric: true },
        { key: 'total_outstanding', label: 'Total Outstanding', numeric: true },
        { key: 'not_due', label: 'Not Due', numeric: true },
        { key: 'total_overdue', label: 'Total Overdue', numeric: true },
        { key: 'bucket_1_30', label: '1–30', numeric: true },
        { key: 'bucket_31_60', label: '31–60', numeric: true },
        { key: 'bucket_61_90', label: '61–90', numeric: true },
        { key: 'bucket_91_120', label: '91–120', numeric: true },
        { key: 'bucket_121_180', label: '121–180', numeric: true },
        { key: 'bucket_over_180', label: '>180', numeric: true },
        { key: 'legal', label: 'Legal', numeric: true },
    ];

const valueMap: Record<AraUserSortKey, keyof AraUserTableRow> = {
    collection_responsibility: 'CollectionResponsibility',
    number_of_bills: 'NumberOfBills',
    total_outstanding: 'TotalOutstanding',
    not_due: 'NotDue',
    total_overdue: 'TotalOverdue',
    bucket_1_30: 'Bucket1_30',
    bucket_31_60: 'Bucket31_60',
    bucket_61_90: 'Bucket61_90',
    bucket_91_120: 'Bucket91_120',
    bucket_121_180: 'Bucket121_180',
    bucket_over_180: 'BucketOver180',
    legal: 'Legal',
};

const AraByUserTable: React.FC<Props> = ({
    data,
    loading,
    customerName,
    onUserClick,
    onBack,
    conversionType,
    onConversionChange,
    page,
    pageSize,
    totalItems,
    onPageChange,
    onPageSizeChange,
    sortBy,
    sortOrder,
    onSortChange,
}) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const formatNumber = (value: number) => {
        let v = value;
        if (conversionType === 'Lakhs') v /= 1e5;
        if (conversionType === 'Crores') v /= 1e7;

        return v.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const renderSkeletonRows = () =>
        Array.from({ length: 8 }).map((_, i) => (
            <tr key={i}>
                {columns.map((_, j) => (
                    <td key={j} className="border px-2 py-1.5">
                        <Skeleton className="h-4 w-full" />
                    </td>
                ))}
            </tr>
        ));

    return (
        <div className="flex flex-col h-full min-h-0">
            {/* Header */}
            <div className="shrink-0 pb-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={onBack}
                            className="px-3 py-1 text-sm rounded bg-gray-200 hover:bg-gray-300"
                        >
                            ← Back
                        </button>
                        <h2 className="text-lg font-semibold">
                            {customerName} — Collection Responsibility
                        </h2>
                    </div>

                    <div className="flex gap-2">
                        {(['INR', 'Lakhs', 'Crores'] as const).map(t => (
                            <button
                                key={t}
                                onClick={() => onConversionChange(t)}
                                className={`px-3 py-1 text-sm rounded ${conversionType === t
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-blue-100 text-blue-700'
                                    }`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto min-h-0 border border-gray-200">
                <table className="w-full text-sm border-collapse">
                    <thead className="sticky top-0 bg-gray-100">
                        <tr>
                            {columns.map(col => {
                                const isActive = sortBy === col.key;

                                return (
                                    <th
                                        key={col.key}
                                        onClick={() => onSortChange(col.key)}
                                        className={`border px-2 py-2 font-semibold cursor-pointer select-none ${col.numeric ? 'text-right' : 'text-left'
                                            }`}
                                    >
                                        <div className="flex items-center gap-1 justify-end">
                                            {!col.numeric && <span className="mr-auto">{col.label}</span>}
                                            {col.numeric && <span>{col.label}</span>}
                                            {isActive ? (
                                                sortOrder === 'asc'
                                                    ? <ChevronUp size={14} />
                                                    : <ChevronDown size={14} />
                                            ) : (
                                                <ChevronUp size={14} className="text-gray-300" />
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            renderSkeletonRows()
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="py-16 text-center text-gray-500">
                                    No data found
                                </td>
                            </tr>
                        ) : (
                            data.map((row, idx) => {
                                const isTotal = row.CollectionResponsibilityEcode === null;

                                return (
                                    <tr
                                        key={idx}
                                        onClick={() => {
                                            if (!isTotal && row.CollectionResponsibilityEcode) {
                                                onUserClick(row.CollectionResponsibilityEcode, row.CollectionResponsibility);
                                            }
                                        }}
                                        className={`
                                            ${idx === 0 ? 'bg-blue-50 font-semibold' : 'hover:bg-gray-50'}
                                            ${!isTotal ? 'cursor-pointer' : ''}
                                        `}
                                    >
                                        {columns.map(col => {
                                            const value = row[valueMap[col.key]];

                                            if (col.key === 'collection_responsibility') {
                                                return (
                                                    <td key={col.key} className="border px-2 py-1.5">
                                                        {isTotal || idx === 0 ? (
                                                            value
                                                        ) : (
                                                            <span className="text-blue-600">{value}</span>
                                                        )}
                                                    </td>
                                                );
                                            }

                                            return (
                                                <td key={col.key} className="border px-2 py-1.5 text-right">
                                                    {col.key === 'number_of_bills' ? value : formatNumber(value as number)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalItems > 0 && (
                <div className="flex items-center justify-between shrink-0 pt-4">
                    <span className="text-sm text-gray-600">
                        Showing{' '}
                        <span className="font-semibold">{(page - 1) * pageSize + 1}</span> to{' '}
                        <span className="font-semibold">{Math.min(page * pageSize, totalItems)}</span> of{' '}
                        <span className="font-semibold">{totalItems}</span> results
                    </span>

                    <div className="flex items-center gap-3 text-sm space-x-10">
                        <div className="flex items-center gap-1">
                            <span className="text-gray-600 mr-1">Rows per page</span>
                            <select
                                value={pageSize}
                                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                                className="border border-gray-300 rounded px-2 py-1"
                            >
                                {[10, 20, 50, 100].map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <span className="text-gray-600">
                            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                        </span>

                        <div className="flex items-center gap-1">
                            <button
                                disabled={page === 1}
                                onClick={() => onPageChange(1)}
                                className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                            >
                                <ChevronsLeft size={16} />
                            </button>

                            <button
                                disabled={page === 1}
                                onClick={() => onPageChange(page - 1)}
                                className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                            >
                                <ChevronLeft size={16} />
                            </button>

                            <button
                                disabled={page === totalPages}
                                onClick={() => onPageChange(page + 1)}
                                className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                            >
                                <ChevronRight size={16} />
                            </button>

                            <button
                                disabled={page === totalPages}
                                onClick={() => onPageChange(totalPages)}
                                className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                            >
                                <ChevronsRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AraByUserTable;
